import React, { useEffect, useState } from 'react';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import classNames from 'classnames';
import styles from './AssetThumbnail.module.scss';

export type AssetContext = 'gym' | 'exercise' | 'program' | 'station' | 'trainer';

interface AssetThumbnailProps {
  src?: string | null;
  context: AssetContext;
  alt: string;
  className?: string;
  imageClassName?: string;
  fit?: 'cover' | 'contain';
}

const icons: Record<AssetContext, React.ReactNode> = {
  gym: <BusinessOutlinedIcon />,
  exercise: <FitnessCenterOutlinedIcon />,
  program: <CalendarMonthOutlinedIcon />,
  station: <SensorsOutlinedIcon />,
  trainer: <PersonOutlineOutlinedIcon />,
};

const cleanSrc = (rawSrc?: string | null): string | null => {
  if (!rawSrc || typeof rawSrc !== 'string') return null;
  let trimmed = rawSrc.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('url(') && trimmed.endsWith(')')) {
    trimmed = trimmed.slice(4, -1).trim();
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      trimmed = trimmed.slice(1, -1).trim();
    }
  }
  return trimmed || null;
};

const AssetThumbnail: React.FC<AssetThumbnailProps> = ({
  src,
  context,
  alt,
  className,
  imageClassName,
  fit = 'cover',
}) => {
  const normalizedSrc = cleanSrc(src);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [normalizedSrc]);

  return (
    <div
      className={classNames(styles.thumbnail, styles[fit], className)}
      role={!normalizedSrc || failed ? 'img' : undefined}
      aria-label={!normalizedSrc || failed ? `${alt} unavailable` : undefined}
    >
      {normalizedSrc && !failed ? (
        <img
          src={normalizedSrc}
          alt={alt}
          className={classNames(styles.image, imageClassName)}
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          {icons[context]}
        </div>
      )}
    </div>
  );
};

export default AssetThumbnail;
